import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Star, CircleCheck, PenLine } from "lucide-react";
import { ReviewForm } from "@/components/landing/ReviewForm";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const Stars = ({ value, size = "w-4 h-4" }) => (
    <span className="inline-flex gap-0.5" aria-label={`${value} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} className={`${size} ${n <= Math.round(value) ? "fill-gold-500 text-gold-500" : "text-forest-200"}`} />
        ))}
    </span>
);

export default function CustomerReviews({ product, summary }) {
    const [params] = useSearchParams();
    const wantsReview = params.get("review") === "1";
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const { count, average, reviews } = summary;

    useEffect(() => {
        if (!wantsReview) return;
        ref.current?.scrollIntoView({ behavior: "smooth" });
        setOpen(true);
    }, [wantsReview]);
    return (
        <section ref={ref} id="customer-reviews" data-testid="customer-reviews" className="py-14 sm:py-20 bg-white border-y border-forest-100">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
                    <div>
                        <h2 className="font-serif font-bold text-forest-800 text-3xl sm:text-4xl">Customer photo reviews</h2>
                        <div data-testid="reviews-summary" className="flex items-center gap-3 mt-3 text-sm text-forest-900/70">
                            {count > 0 ? (
                                <>
                                    <Stars value={average} />
                                    <span className="font-bold text-forest-900">{average.toFixed(1)}</span>
                                    <span>from {count} review{count > 1 ? "s" : ""}</span>
                                </>
                            ) : (
                                <span>Be the first to share your results.</span>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        data-testid="write-review-btn"
                        onClick={() => setOpen(true)}
                        className="inline-flex items-center gap-2 bg-forest-800 hover:bg-forest-900 text-gold-400 font-bold text-sm px-6 py-3 rounded-full border border-gold-500/40 transition-colors"
                    >
                        <PenLine className="w-4 h-4" /> Write a review
                    </button>
                </div>
                {reviews.length > 0 && (
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {reviews.slice(0, 9).map((r) => (
                            <ReviewCard key={r.id} review={r} />
                        ))}
                    </div>
                )}
            </div>
            <ReviewForm product={product} open={open} onClose={() => setOpen(false)} initialOrder={params.get("order") || ""} />
        </section>
    );
}

function ReviewCard({ review }) {
    return (
        <article data-testid={`review-card-${review.id}`} className="bg-cream rounded-2xl border border-forest-100 p-5 flex flex-col">
            <div className="flex items-center justify-between">
                <Stars value={review.rating} />
                {review.verified && (
                    <span data-testid="verified-buyer-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-forest-700">
                        <CircleCheck className="w-3.5 h-3.5 fill-forest-700 text-cream" /> Verified buyer
                    </span>
                )}
            </div>
            <p className="text-sm text-forest-900/80 mt-3 leading-relaxed flex-1">“{review.text}”</p>
            {review.photo_urls?.length > 0 && (
                <div className="flex gap-2 mt-4">
                    {review.photo_urls.map((u) => (
                        <a key={u} href={`${API_BASE}${u}`} target="_blank" rel="noreferrer">
                            <img src={`${API_BASE}${u}`} alt={`Review photo by ${review.name}`} loading="lazy" className="w-16 h-16 rounded-lg object-cover border border-forest-100" />
                        </a>
                    ))}
                </div>
            )}
            <p className="text-xs font-bold text-forest-800 mt-4">
                {review.name}
                {review.city && <span className="font-normal text-forest-700/70"> · {review.city}</span>}
            </p>
        </article>
    );
}
